import type { ExpandedState, SortingState, Updater } from "@tanstack/react-table"
import { type RefObject, useCallback, useMemo } from "react"

import { type GoodsReceiptsSearchFilters, useGoodsReceiptsSearchStore } from "../stores"

import {
  type WarehouseReceivingTableSearchParams,
  type WarehouseReceivingTableStoreSelectors,
  useWarehouseReceivingTableBase,
} from "./useWarehouseReceivingTableBase"

import type {
  GoodsReceipt,
  GoodsReceiptSortField,
  GoodsReceiptStatus,
  Lookup,
  PageInfo,
} from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import type { Option } from "@/types/form-fields"

interface UseGoodsReceiptsTableParams {
  statuses: GoodsReceiptStatus[]
  routePath: "/app/warehouse-receiving" | "/app/tablet/warehouse-receiving" | "/app/procurement"
}

interface UseGoodsReceiptsTableReturn {
  goodsReceipts: GoodsReceipt[]
  isGoodsReceiptsFetching: boolean
  searchQuery: string
  warehouseAreaIds: string[]
  warehouses: Lookup[]
  warehouseOptions: Option<string>[]
  isWarehousesFetching: boolean
  expandedState: ExpandedState
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  pageInfo: PageInfo | undefined
  handleSearchChange: (query: string) => void
  handleFilterChange: (warehouseIds: string[]) => void
  handleFilterRemove: (warehouseId: string) => void
  handleFilterClear: () => void
  handleRefetch: () => void
  handleExpandedChange: (updaterOrValue: Updater<ExpandedState>) => void
  handleSortingChange: (updaterOrValue: Updater<SortingState>) => void
  handleScroll: () => void
}

export const useGoodsReceiptsTable = ({
  statuses,
  routePath,
}: UseGoodsReceiptsTableParams): UseGoodsReceiptsTableReturn => {
  const data = useGoodsReceiptsSearchStore((state) => state.data)
  const fetching = useGoodsReceiptsSearchStore((state) => state.fetching)
  const executeSearch = useGoodsReceiptsSearchStore((state) => state.executeSearch)
  const loadMore = useGoodsReceiptsSearchStore((state) => state.loadMore)
  const clearStore = useGoodsReceiptsSearchStore((state) => state.clearStore)
  const pageInfo = useGoodsReceiptsSearchStore((state) => state.pageInfo)

  const reactiveStoreSelectors: WarehouseReceivingTableStoreSelectors<GoodsReceipt, GoodsReceiptsSearchFilters> =
    useMemo(
      () => ({
        data,
        fetching,
        executeSearch: executeSearch as any,
        loadMore: loadMore as any,
        clearStore,
        pageInfo,
      }),
      [data, fetching, executeSearch, loadMore, clearStore, pageInfo]
    )

  const buildSearchParams = useCallback(
    (baseParams: WarehouseReceivingTableSearchParams): GoodsReceiptsSearchFilters =>
      ({
        ...baseParams,
        statuses,
      }) as GoodsReceiptsSearchFilters,
    [statuses]
  )

  const baseHookReturn = useWarehouseReceivingTableBase<
    GoodsReceipt,
    GoodsReceiptSortField,
    GoodsReceiptsSearchFilters
  >({
    store: reactiveStoreSelectors,
    searchParamsRoute: `${routePath}/_layout`,
    navigationRoute: routePath,
    buildSearchParams,
    isExpandable: true,
    queryClient,
  })

  return {
    goodsReceipts: baseHookReturn.data,
    isGoodsReceiptsFetching: baseHookReturn.isFetching,
    searchQuery: baseHookReturn.searchQuery,
    warehouseAreaIds: baseHookReturn.warehouseAreaIds,
    warehouses: baseHookReturn.warehouses,
    warehouseOptions: baseHookReturn.warehouseOptions,
    isWarehousesFetching: baseHookReturn.isWarehousesFetching,
    expandedState: baseHookReturn.expandedState!,
    sortingState: baseHookReturn.sortingState,
    tableContainerRef: baseHookReturn.tableContainerRef,
    pageInfo: baseHookReturn.pageInfo,
    handleRefetch: baseHookReturn.handleRefetch,
    handleSearchChange: baseHookReturn.handleSearchChange,
    handleFilterChange: baseHookReturn.handleFilterChange,
    handleFilterRemove: baseHookReturn.handleFilterRemove,
    handleFilterClear: baseHookReturn.handleFilterClear,
    handleExpandedChange: baseHookReturn.handleExpandedChange!,
    handleSortingChange: baseHookReturn.handleSortingChange,
    handleScroll: baseHookReturn.handleScroll,
  }
}
